import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Star } from 'lucide-react';

interface VerificationRequestButtonProps {
  hostId: string;
  isVerified: boolean;
  sessionUserId?: string;
}

export const VerificationRequestButton = ({ hostId, isVerified, sessionUserId }: VerificationRequestButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleRating, setGoogleRating] = useState('');
  const [googleReviews, setGoogleReviews] = useState('');
  const [googleLink, setGoogleLink] = useState('');
  const [trustpilotRating, setTrustpilotRating] = useState('');
  const [trustpilotReviews, setTrustpilotReviews] = useState('');
  const [trustpilotLink, setTrustpilotLink] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query to check if there's a pending verification request
  const { data: existingRequest, isLoading: isCheckingRequest } = useQuery({
    queryKey: ['verification-request', hostId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('verification_requests')
        .select('*')
        .eq('host_id', hostId)
        .eq('status', 'pending')
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!hostId,
  });

  // Show verified badge for verified hosts
  if (isVerified) {
    return (
      <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
        <Shield className="w-5 h-5" />
        <span className="font-medium">Verified Host</span>
      </div>
    );
  }

  // Only show request button for non-verified hosts viewing their own profile
  if (!sessionUserId || hostId !== sessionUserId) {
    return null;
  }

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('verification_requests')
        .insert({
          host_id: hostId,
          google_maps_rating: parseFloat(googleRating),
          google_maps_reviews_count: parseInt(googleReviews),
          google_maps_link: googleLink,
          trustpilot_rating: parseFloat(trustpilotRating),
          trustpilot_reviews_count: parseInt(trustpilotReviews),
          trustpilot_link: trustpilotLink,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your verification request has been submitted.",
      });
      
      setIsOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['verification-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['verification-request', hostId] }),
        queryClient.invalidateQueries({ queryKey: ['profile', hostId] }),
        queryClient.invalidateQueries({ queryKey: ['host', hostId] })
      ]);
    } catch (error) {
      console.error('Error submitting verification request:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to submit verification request. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button 
        onClick={() => setIsOpen(true)} 
        variant="outline"
        className="w-full flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-200"
        disabled={!!existingRequest || isCheckingRequest}
      >
        <Shield className="w-5 h-5" />
        {existingRequest ? 'Verification Request Pending' : 'Request Host Verification'}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Request Host Verification
            </DialogTitle>
            <DialogDescription>
              Please provide your external ratings and profile links to request verification.
              This helps us ensure the quality of our host community.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Google Maps Ratings
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="google-rating">Rating (0-5)</Label>
                  <Input
                    id="google-rating"
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={googleRating}
                    onChange={(e) => setGoogleRating(e.target.value)}
                    placeholder="4.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="google-reviews">Review Count</Label>
                  <Input
                    id="google-reviews"
                    type="number"
                    min="0"
                    value={googleReviews}
                    onChange={(e) => setGoogleReviews(e.target.value)}
                    placeholder="100"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="google-link">Google Maps Profile Link</Label>
                <Input
                  id="google-link"
                  type="url"
                  placeholder="https://maps.google.com/..."
                  value={googleLink}
                  onChange={(e) => setGoogleLink(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Star className="w-5 h-5 text-green-500" />
                Trustpilot Ratings
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="trustpilot-rating">Rating (0-5)</Label>
                  <Input
                    id="trustpilot-rating"
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={trustpilotRating}
                    onChange={(e) => setTrustpilotRating(e.target.value)}
                    placeholder="4.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trustpilot-reviews">Review Count</Label>
                  <Input
                    id="trustpilot-reviews"
                    type="number"
                    min="0"
                    value={trustpilotReviews}
                    onChange={(e) => setTrustpilotReviews(e.target.value)}
                    placeholder="100"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="trustpilot-link">Trustpilot Profile Link</Label>
                <Input
                  id="trustpilot-link"
                  type="url"
                  placeholder="https://trustpilot.com/..."
                  value={trustpilotLink}
                  onChange={(e) => setTrustpilotLink(e.target.value)}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLoading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};